import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { AuthContext, type AuthContextValue, type WalletProfile } from './authState';
import { MiniKit } from '@worldcoin/minikit-js';
import {
  CentralSsoError,
  getCentralSsoDiagnosticErrorCode,
  clearCentralSsoTransaction,
  completeCentralSsoCallbackOnce,
  completeCentralSsoPopupCallback,
  hasCentralSsoCallbackResponse,
  isCentralSsoPopupCallbackMessage,
  logCentralSsoDiagnostic,
  startCentralSsoPopup,
} from '../lib/centralSso';
import { supabase } from '../lib/supabaseClient';
import { signInWithWorldWallet, WorldWalletAuthError } from '../lib/worldWalletAuth';
import {
  Flight404WalletError,
  bridgeSupabaseSessionToFlight404,
  bridgeSupabaseSessionToFlight404WithRecovery,
} from '../lib/flight404Wallet';
import { clearWorldIdClaimRecovery } from '../lib/worldIdClaim';
import {
  formatMessageWithCode,
  getErrorCode,
  localizeError,
  translate,
} from '../i18n';

class SessionExpiredError extends Error {
  constructor() {
    super('Bubble Wallet session expired.');
    this.name = 'SessionExpiredError';
  }
}

const PROFILE_SYNC_RETRY_DELAYS_MS = [50, 150];

function isUnauthorizedFunctionError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const context = (error as { context?: unknown }).context;
  if (!context || typeof context !== 'object') return false;
  return (context as { status?: unknown }).status === 401;
}

function getErrorMessage(error: unknown, t: TFunction): string {
  if (error instanceof SessionExpiredError) {
    return t('errors.sessionExpired');
  }
  if (error instanceof CentralSsoError) {
    const messages: Partial<Record<CentralSsoError['code'], string>> = {
      central_sso_not_configured: t('errors.centralSsoNotConfigured'),
      central_sso_issuer_mismatch: t('errors.centralSsoIssuerMismatch'),
      central_sso_storage_failed: t('errors.centralSsoStorageFailed'),
      central_sso_state_mismatch: t('errors.centralSsoStateMismatch'),
      central_sso_missing_callback: t('errors.centralSsoMissingCallback'),
      central_sso_token_exchange_failed: t('errors.centralSsoTokenExchangeFailed'),
      central_sso_invalid_token_response: t('errors.centralSsoInvalidTokenResponse'),
      central_sso_session_failed: t('errors.centralSsoSessionFailed'),
      central_sso_popup_blocked: t('errors.centralSsoPopupBlocked'),
      central_sso_popup_failed: t('errors.centralSsoPopupFailed'),
      central_sso_popup_closed: t('errors.centralSsoPopupClosed'),
      access_denied: t('errors.accessDenied'),
      invalid_request: t('errors.invalidRequest'),
      invalid_client: t('errors.invalidClient'),
      invalid_redirect_uri: t('errors.invalidRedirectUri'),
      invalid_grant: t('errors.invalidGrant'),
      pkce_verification_failed: t('errors.pkceVerificationFailed'),
    };
    return messages[error.code] ?? t('errors.login');
  }
  if (error instanceof WorldWalletAuthError) {
    const messages: Record<WorldWalletAuthError['code'], string> = {
      auth_not_configured: t('errors.walletAuthNotConfigured'),
      world_app_unavailable: t('errors.worldAppUnavailable'),
      world_app_not_configured: t('errors.worldAppNotConfigured'),
      world_app_timeout: t('errors.worldAppTimeout'),
      world_app_network_failed: t('errors.worldAppNetworkFailed'),
      world_app_nonce_failed: t('errors.worldAppNonceFailed'),
      world_app_rejected: t('errors.worldAppRejected'),
      world_app_wallet_missing: t('errors.worldAppWalletMissing'),
      world_app_verification_failed: t('errors.worldAppVerificationFailed'),
      world_app_session_failed: t('errors.worldAppSessionFailed'),
      world_app_sign_in_failed: t('errors.worldAppSignInFailed'),
    };
    return messages[error.code];
  }
  return localizeError(error, t, 'errors.login');
}

function presentAuthError(error: unknown, t: TFunction, fallbackCode = 'LOGIN_FAILED'): string {
  return formatMessageWithCode(getErrorMessage(error, t), t, getErrorCode(error, fallbackCode));
}

function getIsWorldApp(): boolean {
  try {
    return MiniKit.isInstalled();
  } catch {
    return false;
  }
}

async function loadProfile(userId: string): Promise<WalletProfile | null> {
  if (!supabase) {
    return null;
  }

  const { data, error } = await supabase.functions.invoke('get-account-profile', {
    body: {},
  });

  if (isUnauthorizedFunctionError(error) || data?.error === 'Unauthorized') {
    throw new SessionExpiredError();
  }

  if (error || !data?.success || !data.profile || data.profile.id !== userId) {
    throw new Error(translate('errors.loadProfile'));
  }

  return {
    id: data.profile.id,
    username: data.profile.username ?? null,
    nickname: data.profile.nickname ?? null,
    wallet_address: data.profile.wallet_address ?? null,
    last_auth_method: data.profile.last_auth_method ?? null,
  };
}

export function AuthProvider({ children }: PropsWithChildren) {
  const { t } = useTranslation();
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<WalletProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isWorldApp = useMemo(getIsWorldApp, []);
  const latestSessionRef = useRef<Session | null>(null);
  const sessionVersionRef = useRef(0);
  const bridgeErrorRef = useRef<string | null>(null);
  const isSigningOutExpiredSessionRef = useRef(false);
  const isBootstrapCompleteRef = useRef(false);
  const centralSsoPopupRef = useRef<Window | null>(null);
  const centralSsoPopupPollRef = useRef<number | null>(null);
  const centralSsoPopupMessageHandlerRef = useRef<((event: MessageEvent<unknown>) => void) | null>(null);
  const isCentralSsoPopupAttemptInFlightRef = useRef(false);
  const centralSsoPopupAttemptIdRef = useRef(0);
  const activeCentralSsoPopupAttemptIdRef = useRef<number | null>(null);
  const profileSyncPromiseRef = useRef<{
    sessionKey: string;
    promise: Promise<void>;
  } | null>(null);

  const clearCentralSsoPopup = useCallback((attemptId?: number): boolean => {
    if (attemptId !== undefined && activeCentralSsoPopupAttemptIdRef.current !== attemptId) {
      return false;
    }
    if (centralSsoPopupMessageHandlerRef.current) {
      window.removeEventListener('message', centralSsoPopupMessageHandlerRef.current);
      centralSsoPopupMessageHandlerRef.current = null;
    }
    if (centralSsoPopupPollRef.current !== null) {
      window.clearInterval(centralSsoPopupPollRef.current);
      centralSsoPopupPollRef.current = null;
    }
    centralSsoPopupRef.current = null;
    activeCentralSsoPopupAttemptIdRef.current = null;
    return true;
  }, []);

  useEffect(() => () => {
    clearCentralSsoPopup();
    isCentralSsoPopupAttemptInFlightRef.current = false;
  }, [clearCentralSsoPopup]);

  useEffect(() => {
    let isMounted = true;
    isBootstrapCompleteRef.current = false;

    if (!supabase) {
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const isCurrentSession = (expectedSession: Session | null): boolean => (
      latestSessionRef.current?.user.id === expectedSession?.user.id
      && latestSessionRef.current?.access_token === expectedSession?.access_token
    );

    const clearBridgeError = () => {
      const bridgeError = bridgeErrorRef.current;
      bridgeErrorRef.current = null;
      if (bridgeError) {
        setError((currentError) => currentError === bridgeError ? null : currentError);
      }
    };

    const silentlySignOutExpiredSession = async (expectedSession: Session): Promise<void> => {
      if (!isMounted || !isCurrentSession(expectedSession)) return;

      clearWorldIdClaimRecovery();
      bridgeErrorRef.current = null;
      latestSessionRef.current = null;
      sessionVersionRef.current += 1;
      setSession(null);
      setProfile(null);
      setError(null);
      setIsLoading(false);
      void bridgeSupabaseSessionToFlight404(null);
      clearCentralSsoTransaction();

      if (isSigningOutExpiredSessionRef.current) return;
      isSigningOutExpiredSessionRef.current = true;
      try {
        const { error: signOutError } = await supabase!.auth.signOut({ scope: 'local' });
        if (signOutError) {
          console.warn('[Bubble Wallet] Automatic local sign-out failed.');
        }
      } catch {
        console.warn('[Bubble Wallet] Automatic local sign-out failed.');
      } finally {
        isSigningOutExpiredSessionRef.current = false;
      }
    };

    const syncProfile = async (nextSession: Session | null) => {
      if (!nextSession) {
        if (isMounted && isCurrentSession(nextSession)) {
          setProfile(null);
        }
        return;
      }

      const sessionKey = `${nextSession.user.id}:${nextSession.access_token}`;
      const activeProfileSync = profileSyncPromiseRef.current;
      if (activeProfileSync?.sessionKey === sessionKey) {
        return activeProfileSync.promise;
      }

      const syncPromise = (async () => {
        let nextProfile: WalletProfile | null = null;
        let profileError: unknown = null;
        for (let attempt = 0; attempt <= PROFILE_SYNC_RETRY_DELAYS_MS.length; attempt += 1) {
          try {
            nextProfile = await loadProfile(nextSession.user.id);
            profileError = null;
            break;
          } catch (error) {
            profileError = error;
            if (attempt === PROFILE_SYNC_RETRY_DELAYS_MS.length) break;

            const { data: { session: currentSession } } = await supabase!.auth.getSession();
            if (
              !currentSession
              || currentSession.user.id !== nextSession.user.id
              || currentSession.access_token !== nextSession.access_token
            ) {
              break;
            }
            await new Promise<void>((resolve) => window.setTimeout(resolve, PROFILE_SYNC_RETRY_DELAYS_MS[attempt]));
          }
        }

        try {
          if (profileError) throw profileError;
          if (isMounted && isCurrentSession(nextSession)) {
            setProfile(nextProfile);
          }
        } catch (profileError) {
          if (isMounted && isCurrentSession(nextSession)) {
            if (profileError instanceof SessionExpiredError) {
              await silentlySignOutExpiredSession(nextSession);
              return;
            }
            setError(presentAuthError(profileError, t, 'PROFILE_LOAD_FAILED'));
          }
        }

        try {
          const refreshedSession = await bridgeSupabaseSessionToFlight404WithRecovery(nextSession, async () => {
            const { data, error: refreshError } = await supabase!.auth.refreshSession();
            if (refreshError || !data.session) throw refreshError ?? new Error('Session refresh failed.');
            return data.session;
          });
          if (!isMounted) return;

          if (isCurrentSession(nextSession)) {
            if (refreshedSession.access_token !== nextSession.access_token) {
              latestSessionRef.current = refreshedSession;
              sessionVersionRef.current += 1;
              setSession(refreshedSession);
            }
            clearBridgeError();
          } else if (isCurrentSession(refreshedSession)) {
            clearBridgeError();
          }
        } catch (bridgeError) {
          if (
            bridgeError instanceof Flight404WalletError
            && bridgeError.code === 'flight404_jwt_expired'
          ) {
            await silentlySignOutExpiredSession(nextSession);
            return;
          }
          console.warn('[Bubble Wallet] Flight404 Firebase bridge unavailable:', bridgeError);
          if (isMounted && isCurrentSession(nextSession)) {
            const message = presentAuthError(bridgeError, t, 'FLIGHT404_BRIDGE_FAILED');
            bridgeErrorRef.current = message;
            setError(message);
          }
        }
      })();

      profileSyncPromiseRef.current = { sessionKey, promise: syncPromise };
      const clearProfileSyncPromise = () => {
        if (profileSyncPromiseRef.current?.promise === syncPromise) {
          profileSyncPromiseRef.current = null;
        }
      };
      void syncPromise.then(clearProfileSyncPromise, clearProfileSyncPromise);
      return syncPromise;
    };

    const applySession = (nextSession: Session | null) => {
      latestSessionRef.current = nextSession;
      sessionVersionRef.current += 1;
      if (!isMounted) {
        return;
      }

      setSession(nextSession);
      void syncProfile(nextSession);
      if (!nextSession && isBootstrapCompleteRef.current) {
        setIsLoading(false);
      }
    };

    const { data: authListener } = supabase.auth.onAuthStateChange((event, nextSession) => {
      applySession(nextSession);
      if (event === 'TOKEN_REFRESHED') {
        clearBridgeError();
      }
    });

    void (async () => {
      const receivedCentralSsoCallback = hasCentralSsoCallbackResponse();
      logCentralSsoDiagnostic('bootstrap_started', {
        flow: 'bootstrap',
        callbackResponsePresent: receivedCentralSsoCallback,
        worldApp: isWorldApp,
      });
      let callbackError: unknown = null;
      try {
        await completeCentralSsoCallbackOnce(supabase);
      } catch (error) {
        callbackError = error;
        logCentralSsoDiagnostic('bootstrap_callback_completion_failed', {
          flow: 'bootstrap',
          errorCode: getCentralSsoDiagnosticErrorCode(error),
        });
      }

      const sessionVersionBeforeRead = sessionVersionRef.current;
      const { data, error: sessionError } = await supabase.auth.getSession();
      if (!isMounted) {
        return;
      }
      if (sessionError) {
        logCentralSsoDiagnostic('bootstrap_session_read_failed', {
          flow: 'bootstrap',
          errorStatus: typeof sessionError.status === 'number' ? sessionError.status : null,
        });
        setError(formatMessageWithCode(t('errors.loginState'), t, 'LOGIN_STATE_UNAVAILABLE'));
        isBootstrapCompleteRef.current = true;
        setIsLoading(false);
        return;
      }
      if (callbackError) {
        setError(presentAuthError(callbackError, t, 'SSO_CALLBACK_FAILED'));
      }
      const nextSession = sessionVersionRef.current === sessionVersionBeforeRead
        ? data.session
        : latestSessionRef.current;
      logCentralSsoDiagnostic('bootstrap_session_read', {
        flow: 'bootstrap',
        localSessionPresent: Boolean(nextSession),
        callbackErrorPresent: Boolean(callbackError),
      });
      latestSessionRef.current = nextSession;
      setSession(nextSession);
      await syncProfile(nextSession);
      if (!nextSession) {
        logCentralSsoDiagnostic('bootstrap_login_required', {
          flow: 'bootstrap',
          loginMode: isWorldApp ? 'world_wallet' : 'central_sso_popup',
          automaticSsoStarted: false,
          callbackResponsePresent: receivedCentralSsoCallback,
          callbackErrorPresent: Boolean(callbackError),
        });
      }
      if (isMounted) {
        isBootstrapCompleteRef.current = true;
        setIsLoading(false);
      }
    })();

    return () => {
      isMounted = false;
      authListener.subscription.unsubscribe();
    };
  }, [isWorldApp, t]);

  const signIn = useCallback(async () => {
    if (
      isLoading
      || isSigningIn
      || isCentralSsoPopupAttemptInFlightRef.current
    ) {
      return;
    }

    isCentralSsoPopupAttemptInFlightRef.current = true;
    setIsSigningIn(true);
    setError(null);
    let isPopupPending = false;
    try {
      if (isWorldApp) {
        await signInWithWorldWallet();
      } else {
        const attemptId = centralSsoPopupAttemptIdRef.current + 1;
        centralSsoPopupAttemptIdRef.current = attemptId;
        let popupReadySettled = false;
        let popupClosedBeforeReady = false;
        let handlePopupMessage: ((event: MessageEvent<unknown>) => void) | null = null;
        try {
          const popupAttempt = startCentralSsoPopup();
          activeCentralSsoPopupAttemptIdRef.current = attemptId;
          centralSsoPopupRef.current = popupAttempt.popup;
          handlePopupMessage = async (event: MessageEvent<unknown>) => {
            if (
              activeCentralSsoPopupAttemptIdRef.current !== attemptId
              || !popupReadySettled
              || event.origin !== window.location.origin
              || event.source !== centralSsoPopupRef.current
              || !isCentralSsoPopupCallbackMessage(event.data)
            ) {
              return;
            }

            const popup = centralSsoPopupRef.current;
            clearCentralSsoPopup(attemptId);
            popup?.postMessage({
              type: 'bubble-wallet-central-sso-popup-callback-ack-v1',
              state: event.data.callback.state ?? '',
            }, window.location.origin);
            try {
              await completeCentralSsoPopupCallback(supabase!, event.data);
            } catch (callbackError) {
              setError(presentAuthError(callbackError, t, 'SSO_CALLBACK_FAILED'));
            } finally {
              if (centralSsoPopupAttemptIdRef.current === attemptId) {
                isCentralSsoPopupAttemptInFlightRef.current = false;
                setIsSigningIn(false);
              }
            }
          };
          centralSsoPopupMessageHandlerRef.current = handlePopupMessage;
          window.addEventListener('message', handlePopupMessage);
          isPopupPending = true;
          centralSsoPopupPollRef.current = window.setInterval(() => {
            if (activeCentralSsoPopupAttemptIdRef.current !== attemptId) {
              return;
            }
            if (!centralSsoPopupRef.current?.closed) {
              return;
            }
            popupClosedBeforeReady = !popupReadySettled;
            clearCentralSsoPopup(attemptId);
            if (popupReadySettled) {
              clearCentralSsoTransaction();
            }
            setError(presentAuthError(new CentralSsoError('central_sso_popup_closed'), t, 'SSO_POPUP_CLOSED'));
            if (popupReadySettled && centralSsoPopupAttemptIdRef.current === attemptId) {
              isCentralSsoPopupAttemptInFlightRef.current = false;
              setIsSigningIn(false);
            }
          }, 250);
          await popupAttempt.ready;
          popupReadySettled = true;
          if (popupClosedBeforeReady) {
            isPopupPending = false;
            return;
          }
          return;
        } catch (popupStartError) {
          popupReadySettled = true;
          clearCentralSsoPopup(attemptId);
          isPopupPending = false;
          if (
            popupClosedBeforeReady
            || (popupStartError instanceof CentralSsoError && popupStartError.code === 'central_sso_popup_closed')
          ) {
            setError(presentAuthError(popupStartError, t, 'SSO_POPUP_FAILED'));
            return;
          }
          setError(presentAuthError(popupStartError, t, 'SSO_POPUP_FAILED'));
          return;
        }
      }
    } catch (signInError) {
      setError(presentAuthError(signInError, t));
    } finally {
      if (!isPopupPending) {
        isCentralSsoPopupAttemptInFlightRef.current = false;
        setIsSigningIn(false);
      }
    }
  }, [clearCentralSsoPopup, isLoading, isSigningIn, isWorldApp, t]);

  const signOut = useCallback(async () => {
    clearWorldIdClaimRecovery();
    if (!supabase) {
      return;
    }
    setError(null);
    const { error: signOutError } = await supabase.auth.signOut();
    if (signOutError) {
      setError(formatMessageWithCode(t('errors.signOut'), t, 'SIGN_OUT_FAILED'));
      return;
    }
    await bridgeSupabaseSessionToFlight404(null);
    clearCentralSsoTransaction();
  }, [t]);

  const value = useMemo<AuthContextValue>(() => ({
    session,
    profile,
    isLoading,
    isSigningIn,
    isWorldApp,
    error,
    signIn,
    signOut,
    clearError: () => setError(null),
  }), [error, isLoading, isSigningIn, isWorldApp, profile, session, signIn, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
